/**
 * CountBadge — the unread/pending counter that rides on a tab icon, a nav
 * link or a list row.
 *
 * It exists because there were four of these, hand-rolled in four files, at
 * four different sizes: 8px in explore, 9px in the top nav, 10px in the nav
 * link, 11px in profile. All four were below the 12px readable floor, and the
 * 8px one only rendered its number when the count was above 1 — a single
 * unread notification showed an empty coral circle, then jumped to a "2".
 *
 * White on `coral.DEFAULT` is 3.80:1, which fails AA for the numeral, so the
 * fill is `coral.dark` (5.50:1) — the same rule the primary Button follows.
 *
 * `ringColor` draws the cut-out ring used when the badge overlaps an icon; it
 * should be the color the badge sits on, not a border color.
 */
import { View, Text } from 'react-native'
import { COLORS, FONTS } from '@/lib/brand'

/** Diameter that fits a 12px numeral with breathing room on both sides. */
const DIAMETER = 20

type CountBadgeProps = {
  count: number
  /** Counts above this render as `${max}+`. */
  max?: number
  /** When set, draws a 2px ring so the badge reads against a busy icon. */
  ringColor?: string
  testID?: string
}

export function CountBadge({ count, max = 99, ringColor, testID }: CountBadgeProps) {
  if (count <= 0) return null

  return (
    <View
      testID={testID}
      accessibilityRole="text"
      style={{
        minWidth: DIAMETER,
        height: DIAMETER,
        paddingHorizontal: 5,
        borderRadius: DIAMETER / 2,
        backgroundColor: COLORS.coral.dark,
        alignItems: 'center',
        justifyContent: 'center',
        ...(ringColor ? { borderWidth: 2, borderColor: ringColor } : null),
      }}
    >
      <Text
        style={{
          fontSize: 12,
          lineHeight: 16,
          fontFamily: FONTS.bodyBold,
          color: '#FFFFFF',
          fontVariant: ['tabular-nums'],
        }}
        numberOfLines={1}
      >
        {count > max ? `${max}+` : count}
      </Text>
    </View>
  )
}
